<template>
  <div class="neg-quadro">
    <div v-if="avisos.length" class="neg-aviso">
      <q-icon name="speed" size="18px" />
      <span>{{ avisos.join(" · ") }}</span>
    </div>

    <div v-if="etapaEscondidaDoQuadro" class="neg-vazio neg-vazio--board">
      <q-icon name="visibility_off" size="38px" />
      <div>A etapa {{ etapaFiltrada.nome }} está escondida do quadro.</div>
      <span class="neg-link" @click="$emit('ver-lista', etapaFiltrada.id)">
        Ver as propostas dela na lista
      </span>
    </div>

    <template v-else>
      <div v-if="telaEstreita && etapaAtual" class="neg-quadro__titulo-movel">
        <span class="neg-quadro__etapa-nome">{{ etapaAtual.etapa.nome }}</span>
        <span class="neg-quadro__etapa-posicao">
          {{ indiceVisivel + 1 }} de {{ colunasVisiveis.length }}
        </span>
      </div>

      <div
        ref="board"
        class="neg-board"
        :class="{ 'neg-board--movel': telaEstreita }"
        @scroll="atualizarEtapaVisivel"
      >
        <div
          v-for="coluna in colunasVisiveis"
          :key="coluna.etapa.id"
          class="neg-coluna"
          :class="{ 'neg-coluna--alvo': colunaAlvo === coluna.etapa.id, 'neg-coluna--movel': telaEstreita }"
          @dragover.prevent="colunaAlvo = coluna.etapa.id"
          @dragleave="saiDaColuna(coluna.etapa.id)"
          @drop.prevent="solta(coluna)"
        >
          <div class="neg-coluna__faixa" :style="{ background: coluna.etapa.cor || 'var(--neg-azul)' }"></div>
          <div class="neg-coluna__topo">
            <q-icon v-if="coluna.etapa.encerra" name="flag" size="17px" class="neg-coluna__final">
              <q-tooltip>Etapa final</q-tooltip>
            </q-icon>
            <span class="neg-coluna__nome" :title="coluna.etapa.nome">{{ coluna.etapa.nome }}</span>
            <span class="neg-coluna__qtd">{{ coluna.quantidade }}</span>
          </div>
          <div class="neg-coluna__total">{{ formatarMoeda(coluna.total_valor) }}</div>

          <div v-if="excedeu(coluna)" class="neg-coluna__trunc">
            <span>Mostrando {{ limitePorColuna }} de {{ coluna.oportunidades.length }}</span>
            <span class="neg-link" @click.stop="$emit('ver-lista', coluna.etapa.id)">Ver na lista</span>
          </div>

          <div class="neg-coluna__cards">
            <negocios-card
              v-for="oportunidade in cartasDaColuna(coluna)"
              :key="oportunidade.id"
              :oportunidade="oportunidade"
              :arrastavel="podeEditarOportunidade(oportunidade) && !telaEstreita"
              @arrastar="arrastando = $event"
              @fim-arraste="arrastando = null"
              @abrir="$emit('abrir', $event)"
            />
            <div v-if="!coluna.oportunidades.length" class="neg-vazio">
              <q-icon name="inbox" size="38px" />
              <div>Nenhuma oportunidade aqui</div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import negociosMixin from "@/mixins/negocios";

const LIMITE_POR_COLUNA = 50;
const TOTAL_PARA_AVISAR = 500;

export default {
  name: "negocios-quadro",

  emits: ["abrir", "mover", "ver-lista"],
  mixins: [negociosMixin],
  data() {
    return {
      arrastando: null,
      colunaAlvo: null,
      indiceVisivel: 0,
      limitePorColuna: LIMITE_POR_COLUNA
    };
  },
  computed: {
    ...mapGetters(["negociosColunas", "negociosEtapas", "negociosFiltros"]),
    colunasVisiveis() {
      const etapaId = this.negociosFiltros.etapa_id;

      if (!etapaId) {
        return this.negociosColunas;
      }

      return this.negociosColunas.filter(coluna => coluna.etapa.id === Number(etapaId));
    },
    idsDasColunasVisiveis() {
      return this.colunasVisiveis.map(coluna => coluna.etapa.id).join(",");
    },
    etapaAtual() {
      return this.colunasVisiveis[this.indiceVisivel] || this.colunasVisiveis[0] || null;
    },
    totalDeCartas() {
      return this.negociosColunas.reduce((total, coluna) => total + coluna.quantidade, 0);
    },
    etapaFiltrada() {
      const etapaId = this.negociosFiltros.etapa_id;

      if (!etapaId) {
        return null;
      }

      return this.negociosEtapas.find(etapa => etapa.id === Number(etapaId)) || null;
    },
    etapaEscondidaDoQuadro() {
      return Boolean(this.etapaFiltrada && !this.etapaFiltrada.visivel_quadro);
    },
    avisos() {
      const avisos = [];

      if (this.totalDeCartas > TOTAL_PARA_AVISAR) {
        avisos.push(
          "Quadro com " + this.totalDeCartas + " oportunidades. Refine os filtros para acelerar."
        );
      }

      return avisos;
    }
  },
  watch: {
    idsDasColunasVisiveis() {
      this.indiceVisivel = 0;
    }
  },
  methods: {
    atualizarEtapaVisivel() {
      const board = this.$refs.board;

      if (!board || !board.clientWidth) {
        return;
      }

      const indice = Math.round(board.scrollLeft / board.clientWidth);

      this.indiceVisivel = Math.min(Math.max(indice, 0), this.colunasVisiveis.length - 1);
    },
    cartasDaColuna(coluna) {
      return coluna.oportunidades.slice(0, LIMITE_POR_COLUNA);
    },
    excedeu(coluna) {
      return coluna.oportunidades.length > LIMITE_POR_COLUNA;
    },
    saiDaColuna(etapaId) {
      if (this.colunaAlvo === etapaId) {
        this.colunaAlvo = null;
      }
    },
    solta(coluna) {
      this.colunaAlvo = null;

      const oportunidade = this.arrastando;
      this.arrastando = null;

      if (!oportunidade || oportunidade.etapa_id === coluna.etapa.id) {
        return;
      }

      this.$emit("mover", { oportunidade, etapa: coluna.etapa });
    }
  }
};
</script>
